import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { Progress } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

export interface EmailBulkProgressState {
  title: string;
  statusLine: string;
  done: number;
  total: number;
}

interface EmailBulkProgressModalProps {
  progress: EmailBulkProgressState | null;
}

const EmailBulkProgressModal: React.FC<EmailBulkProgressModalProps> = ({ progress }) => {
  const { colors } = useTheme();

  const { percent, detailLine } = useMemo(() => {
    if (!progress) {
      return { percent: 0, detailLine: '' };
    }
    const pct =
      progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
    return {
      percent: pct,
      detailLine: `${progress.done.toLocaleString()} of ${progress.total.toLocaleString()}`,
    };
  }, [progress]);

  if (!progress) return null;

  return (
    <Modal isOpen closeOnEscape={false} onClose={() => {}} size="sm" zIndex={70}>
      <ModalHeader>
        <ModalTitle>{progress.title}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin text-indigo-500 mt-0.5" aria-hidden />
            <div className="min-w-0 flex-1 space-y-1">
              <p className={cn('text-sm font-medium', colors.text)}>{progress.statusLine}</p>
              <p className={cn('text-xs tabular-nums', colors.textMuted)}>{detailLine}</p>
            </div>
            <span className={cn('text-sm tabular-nums font-medium shrink-0', colors.textSecondary)}>
              {percent}%
            </span>
          </div>
          <Progress value={percent} max={100} size="lg" />
          <p className={cn('text-xs rounded-lg border px-3 py-2', colors.border, colors.surfaceHover, colors.textMuted)}>
            Please keep this tab open until the operation finishes.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default EmailBulkProgressModal;
